/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useTranslation } from '@scuf/localization';
import React, { memo, useContext } from 'react';
import descriptors from '../../../locales/en-US/descriptors';
import DetailSummaryComponent from '../../shared/DetailSummaryComponent/DetailSummaryComponent';
import TrendChart from '../../shared/TrendChart/TrendChart';
import '../../../scss/ViewDetailsTheme.scss';
import { convertDataForTrendChart } from '../../shared/Utils/Utils';
import {
  IConvertDataForUtilityTrendChart,
  IUtilityBillData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  PERCENT_CHANGE_THRESHOLD,
  UtilityType
} from '../../shared/Utils/Constants';
import { ApiContext } from '../../../context/ApiContext';

interface IAllUtilitiesDetailComponentProps {
  data: IUtilityBillData;
  isPrevPeriodForAllMonths?: boolean;
}
const AllUtilitiesDetailComponent = (
  props: IAllUtilitiesDetailComponentProps
) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;

  const [t, lng] = useTranslation();
  const apiContext = useContext(ApiContext);
  const { data, isPrevPeriodForAllMonths } = props;
  const {
    utilityMeasurementUnit,
    utilityCostCurrency,
    totalAggregations,
    monthwiseAggregations,
    totalSiteCount
  } = data;

  const {
    totalActualCost,
    previousPeriodCost,
    totalCostSavings,
    totalCostSavingsPercentage
  } = totalAggregations;

  const savingPercent =
    apiContext.showChangeWhenData &&
    (isNaN(Number(totalActualCost)) ||
      isNaN(Number(previousPeriodCost)) ||
      Number(totalActualCost) === 0 ||
      Number(previousPeriodCost) === 0 ||
      Math.abs(Number(totalCostSavingsPercentage)) > PERCENT_CHANGE_THRESHOLD ||
      !isPrevPeriodForAllMonths);

  const aggregationSummaryData = {
    actualCost: totalActualCost,
    baselineCost: previousPeriodCost,
    costSavings: savingPercent ? null : totalCostSavings,
    costSavingsPercentage: savingPercent ? null : totalCostSavingsPercentage
  };

  const chartData = convertDataForTrendChart(
    (monthwiseAggregations as unknown) as IConvertDataForUtilityTrendChart[],
    totalSiteCount,
    lng
  );

  const legendItems = [
    {
      text: `${t(d.PREVIOUSCOST)}`,
      color: '#606061',
      className: 'emaas-legend-item-bar-baseline',
      type: 'Bar'
    },
    {
      text: `${t(d.ACTUALCOST)}`,
      color: '#1272B2',
      className: 'emaas-legend-item-bar-actual',
      type: 'Bar'
    }
  ];
  return (
    <>
      <DetailSummaryComponent
        aggregationData={aggregationSummaryData}
        costCurrency={utilityCostCurrency}
        measurementUnit={utilityMeasurementUnit}
        detailType={t(e.UTILITYDETAILKEY)}
      />
      <TrendChart
        measurementUnit={utilityMeasurementUnit}
        costCurrency={utilityCostCurrency}
        chartData={chartData}
        legendItems={legendItems}
        category={UtilityType.All}
        renderedFromCarbon={false}
        showAllTooltips={true}
      />
    </>
  );
};

export default memo(AllUtilitiesDetailComponent);
